import React, { FC, useCallback, useRef } from 'react';
import MuiSwitch, { SwitchProps as MuiSwitchProps } from '@mui/material/Switch';
import { FormControlLabel } from './FormControlLabel';
import { BpDesignSystem } from '../../types';
import { FormHelperText } from './FormHelperText';
import { FormGroup } from './FormGroup';
import { Box } from '../Box';
import { Typography, type TypographyProps } from '../Typography';
import { styled } from '../../styles';
import { Icon } from '../Icon/Icon';
import { CircularProgress } from '../Progress';
import { useBpDesignTheme } from '../../setup';

export interface SwitchProps extends BpDesignSystem.PropsWithChildren<Omit<MuiSwitchProps, 'color' | 'size'>> {
  label?: string;
  subLabel?: string;
  helperText?: string;
  error?: boolean;
  /* @default: medium */
  size?: 'small' | 'medium';
  errorText?: string;
  /* @default: false */
  disableIcon?: boolean;
  loading?: boolean;
  /* @default: right */
  labelPlacement?: 'left' | 'right';
  slotProps?: {
    label?: TypographyProps;
    subLabel?: TypographyProps;
  };
}

// Designs for the Switch component can be found here:
// https://www.figma.com/design/K1pSvddIODVaPFImR68zHB/Professional-Design-System?node-id=17929-56157&t=WhHCxSCQa1BOjiL3-0
// https://www.figma.com/design/K1pSvddIODVaPFImR68zHB/Professional-Design-System?node-id=17929-2913&t=T2o8l7xs1lGHz12S-0
export const Switch: FC<SwitchProps> = ({
  dataTest,
  label,
  helperText,
  error,
  errorText,
  disabled,
  loading,
  disableIcon = false,
  subLabel,
  labelPlacement = 'right',
  sx,
  size = 'small',
  checked,
  className = '',
  style,
  required,
  slotProps,
  ...props
}) => {
  const rippleRef = useRef<HTMLButtonElement>(null);
  const disableSwitch = disabled || loading;
  const { tokens } = useBpDesignTheme();

  const disableFontColor = disableSwitch ? tokens.stateOnSurfaceDisabledContentStateLayerColor : undefined;

  const onRippleStart = useCallback(() => {
    if (rippleRef.current) {
      rippleRef.current.classList.add('ripple-start');
    }
  }, []);

  const onRippleEnd = useCallback(() => {
    if (rippleRef.current) {
      rippleRef.current.classList.remove('ripple-start');
    }
  }, []);

  return (
    <StyledFormGroup $position={labelPlacement} style={style} sx={sx} className={className}>
      <FormControlLabel
        labelPlacement={labelPlacement === 'right' ? 'end' : 'start'}
        required={required}
        control={
          <MuiSwitch
            ref={rippleRef}
            className={`checkmark ${checked ? 'checked' : ''} ${!disableIcon ? 'with-icon' : 'without-icon'}`}
            size={size}
            onMouseDown={onRippleStart}
            onMouseUp={onRippleEnd}
            icon={
              !loading ? (
                <Icon
                  style={{ color: !disableIcon ? undefined : 'transparent' }}
                  name={!disableIcon ? 'close' : 'circle'}
                  size={size === 'medium' ? 16 : 12}
                />
              ) : (
                <CircularProgress size={size === 'medium' ? 16 : 12} />
              )
            }
            checked={checked ?? false}
            checkedIcon={
              !loading ? (
                <Icon
                  style={{ color: !disableIcon ? undefined : 'transparent' }}
                  name={!disableIcon ? 'check' : 'circle'}
                  size={size === 'medium' ? 16 : 12}
                />
              ) : (
                <CircularProgress size={size === 'medium' ? 16 : 12} />
              )
            }
            data-test={dataTest}
            disabled={disableSwitch}
            {...props}
          />
        }
        slots={{
          typography: 'span'
        }}
        label={
          label ? (
            <Box className="typography-label">
              <Typography color={disableFontColor} variant="label" large {...slotProps?.label}>
                {label}
              </Typography>
              {subLabel && (
                <Typography color={disableFontColor} variant="label" small {...slotProps?.subLabel}>
                  {subLabel}
                </Typography>
              )}
            </Box>
          ) : undefined
        }
      />

      <FormHelperText helperText={helperText} errorText={errorText} error={error} />
    </StyledFormGroup>
  );
};

const StyledFormGroup = styled(FormGroup)<{ $position: 'left' | 'right' }>`
  .MuiFormControlLabel-root {
    min-height: 24px;
    justify-items: center;
    margin-left: 0;
  }
    
  .MuiSwitch-root.MuiSwitch-sizeSmall.with-icon {
      top: 0;
  }  
    
  .MuiSwitch-root + div {
    position: relative
  }
    
  .MuiFormControlLabel-asterisk {
    position: absolute;
    top: -6px;
    right: 0;
  }
  .typography-label {
    margin: 0 ${({ theme }) => theme.spacingSize.m};
  }

  .MuiFormHelperText-root {
    text-align: ${({ $position }) => ($position === 'left' ? 'right' : 'left')};
  }
`;
